import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { GoogleMap } from '@react-google-maps/api';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { db } from '../../../firebase';
import { useLanguage } from '../../../context/LanguageContext.jsx';
import { TranslatedText } from '../../i18n/TranslatedText.jsx';
import './PendingEventDetail.css';

const PendingEventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isEnglish } = useLanguage();

  const [event, setEvent] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [message, setMessage] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const snap = await getDoc(doc(db, 'events', eventId));
        if (snap.exists()) setEvent({ id: snap.id, ...snap.data() });
      } catch (error) {
        console.error('Error fetching event:', error);
      }
    };
    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    return () => {
      if (markerRef.current) markerRef.current.setMap(null);
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google || !event?.coordinates) return;

    if (markerRef.current) markerRef.current.setMap(null);

    try {
      markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: event.coordinates.lat, lng: event.coordinates.lng },
        map: mapRef.current,
      });
    } catch (error) {
      console.error('Error creating marker:', error);
    }
  }, [event?.coordinates]);

  const handleNext = () => {
    const len = Array.isArray(event?.mediaUrls) ? event.mediaUrls.length : 0;
    if (len <= 1) return;
    setCurrentMediaIndex((prev) => (prev + 1) % len);
  };

  const handlePrev = () => {
    const len = Array.isArray(event?.mediaUrls) ? event.mediaUrls.length : 0;
    if (len <= 1) return;
    setCurrentMediaIndex((prev) => (prev - 1 + len) % len);
  };

  const handleApprove = async () => {
    try {
      await updateDoc(doc(db, 'events', eventId), { status: 'approved' });
      setMessage(isEnglish ? 'Event approved successfully.' : 'Etkinlik başarıyla onaylandı.');
      setTimeout(() => navigate('/içerik-onayla'), 1500);
    } catch (error) {
      setMessage((isEnglish ? 'Error approving event: ' : 'Etkinlik onaylanırken hata: ') + error.message);
    }
  };

  const submitRejection = async () => {
    if (!rejectionReason.trim()) {
      setMessage(isEnglish ? 'Please provide a reason for rejection.' : 'Lütfen reddetme sebebi belirtin.');
      return;
    }
    try {
      await updateDoc(doc(db, 'events', eventId), {
        status: 'rejected',
        rejectionReason: rejectionReason.trim(),
      });
      setMessage(isEnglish ? 'Event rejected.' : 'Etkinlik reddedildi.');
      setShowRejectionModal(false);
      setTimeout(() => navigate('/içerik-onayla'), 1500);
    } catch (error) {
      setMessage((isEnglish ? 'Error rejecting event: ' : 'Etkinlik reddedilirken hata: ') + error.message);
    }
  };

  if (!event) return <div>{isEnglish ? 'Loading...' : 'Yükleniyor...'}</div>;

  const mediaUrls = Array.isArray(event.mediaUrls) ? event.mediaUrls : [];
  const currentUrl = mediaUrls[currentMediaIndex] || '';
  const isVideo = typeof currentUrl === 'string' && currentUrl.includes('.mp4');

  return (
    <div className="pending-event-detail">
      {message && <div className="message">{message}</div>}

      <div className="media-preview">
        <button className="nav-button" onClick={handlePrev} aria-label={isEnglish ? 'Previous' : 'Önceki'}>
          <ArrowLeft size={18} aria-hidden="true" focusable="false" />
        </button>

        {mediaUrls.length > 0 && (
          <div className="media-container">
            {isVideo ? (
              <video className="media-items" controls>
                <source src={currentUrl} />
              </video>
            ) : (
              <img src={currentUrl} alt={event.name} className="media-items" />
            )}
          </div>
        )}

        <button className="nav-button" onClick={handleNext} aria-label={isEnglish ? 'Next' : 'Sonraki'}>
          <ArrowRight size={18} aria-hidden="true" focusable="false" />
        </button>
      </div>

      <div className="pending-map-container">
        <GoogleMap
          onLoad={(map) => {
            mapRef.current = map;
            if (!event?.coordinates || !window.google) return;
            try {
              markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
                position: { lat: event.coordinates.lat, lng: event.coordinates.lng },
                map,
              });
            } catch (error) {
              console.error('Error creating marker:', error);
            }
          }}
          mapContainerClassName="pending-map"
          center={event.coordinates}
          zoom={15}
          options={{ mapId: '6100ebed9733d3ab' }}
        />
      </div>

      <h2>
        <TranslatedText text={event.name} dynamic />
      </h2>

      <div className="submitter-info">
        <p>
          <strong>{isEnglish ? 'Submitted by:' : 'Gönderen:'}</strong> {event.userEmail || (isEnglish ? 'Email not available' : 'E-posta yok')}
        </p>
      </div>

      <p>
        <strong>{isEnglish ? 'Organizer:' : 'Organizatör:'}</strong> <TranslatedText text={event.organizer} dynamic />
      </p>
      <p>
        <strong>{isEnglish ? 'Location:' : 'Konum:'}</strong> <TranslatedText text={event.location} dynamic />
      </p>
      <p>
        <strong>{isEnglish ? 'Date:' : 'Tarih:'}</strong> {event.date}
      </p>
      <p>
        <strong>{isEnglish ? 'Capacity:' : 'Kontenjan Sınırı:'}</strong> {event.maxCapacity || (isEnglish ? 'Unlimited' : 'Sınırsız')}
      </p>
      <p>
        <strong>{isEnglish ? 'Age range:' : 'Yaş Aralığı:'}</strong>{' '}
        {!event.ageRange
          ? isEnglish
            ? 'All ages'
            : 'Her Yaşa Uygundur'
          : event.ageRange === 'all'
            ? 'All Ages'
            : event.ageRange}
      </p>

      <p>
        <strong>{isEnglish ? 'Contact:' : 'İletişim Bilgileri:'}</strong>
      </p>
      <div>
        {(event.contactInfo || '').split('\n').map((line, index) => (
          <p key={index}>
            <TranslatedText text={line} dynamic />
          </p>
        ))}
      </div>

      <p>
        <strong>{isEnglish ? 'About this event:' : 'Etkinlik Bilgileri:'}</strong>
      </p>
      <div>
        {(event.description || '').split('\n').map((line, index) => (
          <p key={index}>
            <TranslatedText text={line} dynamic />
          </p>
        ))}
      </div>

      <div className="confirmation-buttons">
        <button className="approve-button" onClick={handleApprove}>
          {isEnglish ? 'Approve' : 'Onaylayın'}
        </button>
        <button className="disapprove-button" onClick={() => setShowRejectionModal(true)}>
          {isEnglish ? 'Reject' : 'Reddedin'}
        </button>
      </div>

      {showRejectionModal && (
        <div className="rejection-modal">
          <h3>{isEnglish ? 'Rejection reason' : 'Reddetme Sebebi'}</h3>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder={isEnglish ? 'Enter reason for rejection' : 'Reddetme sebebini yazın'}
          />
          <div className="modal-buttons">
            <button onClick={submitRejection}>{isEnglish ? 'Submit' : 'Gönder'}</button>
            <button onClick={() => setShowRejectionModal(false)}>{isEnglish ? 'Cancel' : 'İptal'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingEventDetail;
